import { Component, OnInit, Input, OnDestroy, inject } from '@angular/core';
import { FormGroup, ControlContainer, FormGroupDirective } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { startWith, filter, map } from 'rxjs/operators';
import { CropData } from 'src/app/shared/models';

@Component({
  selector: 'app-preceding-crop-settings',
  templateUrl: './preceding-crop-settings.component.html',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  standalone: false,
})
export class PrecedingCropSettingsComponent implements OnInit, OnDestroy {
  parentFormGroup = inject(FormGroupDirective);

  @Input() precedingCropFormGroup: FormGroup;
  @Input() crops: CropData[];

  filteredCrops$: Observable<CropData[]>;
  selectedCrop: CropData;

  private subscription = new Subscription();

  ngOnInit() {
    this.filteredCrops$ = this.precedingCropFormGroup.controls.cropId.valueChanges.pipe(
      startWith(''),
      filter((value) => typeof value === 'string'),
      map((value: string) => this.filterCrops(value)),
    );
    this.registerFormEvents();

    this.subscription.add(
      this.precedingCropFormGroup.controls.cropId.valueChanges.subscribe((cropId: number) => {
        if (cropId) {
          const crop = this.findCrop(cropId);
          if (!crop) {
            this.precedingCropFormGroup.controls.cropId.setValue(null, {
              emitModelToViewChange: false,
            });
          }
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  getCropId() {
    return this.precedingCropFormGroup.get('cropId').value as number;
  }

  displayCrop() {
    return (cropId?: number) => (cropId ? this.findCrop(cropId)?.agrifirmCrop.name : '');
  }

  selectCrop(cropId: number) {
    this.selectedCrop = this.findCrop(cropId);
  }

  checkCrop() {
    if (!this.selectedCrop) {
      this.precedingCropFormGroup.get('cropId').setValue(null);
    }
  }

  private filterCrops(value: string): CropData[] {
    const filterValue = value.toLowerCase();
    return this.crops.filter((option) =>
      option.agrifirmCrop.name.toLowerCase().includes(filterValue),
    );
  }

  private findCrop(cropId: number): CropData {
    return this.crops.find((crop) => crop.id === cropId);
  }

  private updateDefaultNitrogenDeliveryAmount() {
    const cropId = this.precedingCropFormGroup.get('cropId').value;
    if (typeof cropId === 'string' || !(cropId as number)) {
      return;
    }

    const crop = this.crops.find((c) => c.id === cropId);
    this.precedingCropFormGroup
      .get('nitrogenDeliveryAmount')
      .setValue(crop.defaultNitrogenFertilization, { emitEvent: false });
  }

  private registerFormEvents() {
    this.precedingCropFormGroup.get('cropId').valueChanges.subscribe(() => {
      this.updateDefaultNitrogenDeliveryAmount();
      this.precedingCropFormGroup
        .get('isNitrogenDeliveryAmountSetManually')
        .setValue(false, { emmitEvent: false });
    });
    this.precedingCropFormGroup
      .get('isNitrogenDeliveryAmountSetManually')
      .valueChanges.subscribe((isNitrogenDeliveryAmountSetManually) => {
        if (!isNitrogenDeliveryAmountSetManually) {
          this.updateDefaultNitrogenDeliveryAmount();
        }
      });

    this.precedingCropFormGroup
      .get('nitrogenDeliveryAmount')
      .valueChanges.subscribe(() =>
        this.precedingCropFormGroup
          .get('isNitrogenDeliveryAmountSetManually')
          .setValue(true, { emmitEvent: false }),
      );
  }
}
